/**
 * Durasan — lista ulubionych produktów.
 *
 * Trzyma uchwyty (handle) produktów w localStorage, synchronizuje serduszka na
 * kartach produktów, licznik w nagłówku i widok strony /pages/ulubione.
 * Działa bez konta klienta i bez dodatkowych aplikacji.
 */
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'durasan:wishlist:v1';
const MAX_ITEMS = 100;

/** @returns {string[]} lista handli produktów */
function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h.length > 0) : [];
  } catch {
    // Prywatne okno albo zapełniony storage — lista ulubionych jest wtedy pusta,
    // ale reszta sklepu musi działać dalej.
    return [];
  }
}

/** @param {string[]} items */
function write(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    /* brak miejsca lub zablokowany storage — ignorujemy */
  }
  document.dispatchEvent(new CustomEvent('durasan:wishlist:change', { detail: { items } }));
}

function has(handle) {
  return read().includes(handle);
}

function toggle(handle) {
  const items = read();
  const index = items.indexOf(handle);

  if (index === -1) {
    items.unshift(handle);
  } else {
    items.splice(index, 1);
  }

  write(items);
  return index === -1;
}

function remove(handle) {
  write(read().filter((item) => item !== handle));
}

/** Teksty z pliku tłumaczeń, wstrzykiwane przez snippet 'durasan-wishlist-data'. */
function t(key) {
  const strings = window.DurasanWishlistStrings || {};
  return strings[key] || '';
}

function money(cents) {
  const format = window.DurasanShop?.moneyFormat;
  const amount = cents / 100;

  // Preferujemy natywne formatowanie waluty sklepu — daje poprawne spacje i przecinek.
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'pl-PL', {
      style: 'currency',
      currency: window.DurasanShop?.currency || 'PLN',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return format ? format.replace(/\{\{\s*amount\s*\}\}/, amount.toFixed(2)) : `${amount.toFixed(2)}`;
  }
}

/* ------------------------------------------------------------------ */
/* Serduszka na kartach produktów                                       */
/* ------------------------------------------------------------------ */

function syncButton(button) {
  const handle = button.dataset.productHandle;
  if (!handle) return;

  const active = has(handle);
  button.setAttribute('aria-pressed', String(active));

  const label = active ? t('remove') : t('add');
  if (label) {
    const title = button.dataset.productTitle || '';
    const text = title ? `${label}: ${title}` : label;
    button.setAttribute('aria-label', text);
    button.setAttribute('title', label);
  }
}

function syncAllButtons(root = document) {
  root.querySelectorAll('[data-du-wish]').forEach(syncButton);
}

function syncCounters() {
  const count = read().length;

  document.querySelectorAll('[data-du-wish-count]').forEach((el) => {
    el.textContent = count > 99 ? '99+' : String(count);
    el.toggleAttribute('hidden', count === 0);
  });
}

/* ------------------------------------------------------------------ */
/* Strona „Ulubione"                                                    */
/* ------------------------------------------------------------------ */

async function fetchProduct(handle) {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(`Nie znaleziono produktu: ${handle}`);
  const product = await response.json();
  const variant = product.variants.find((item) => item.available) || product.variants[0];
  if (variant?.compare_at_price > variant?.price) {
    // Use the same public, variant-specific Liquid renderer as the catalog.
    // Ajax product JSON does not include the price-history metafield.
    product.duPriceHistory = 'Historia cen jest w trakcie zbierania.';
    const historyController = new AbortController();
    const historyTimeout = window.setTimeout(() => historyController.abort(), 8000);
    try {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('section_id', 'section-rendering-product-card');
      url.searchParams.set('variant', String(variant.id));
      const historyResponse = await fetch(url, { headers: { Accept: 'text/html' }, signal: historyController.signal });
      if (historyResponse.ok) {
        const historyDocument = new DOMParser().parseFromString(await historyResponse.text(), 'text/html');
        const history = historyDocument.querySelector('[data-du-price-history]');
        if (history?.dataset.variantId === String(variant.id)) {
          product.duPriceHistory = history.textContent.replace(/\s+/g, ' ').trim();
        }
      }
    } catch {
      // A failed history request never substitutes the current price as history.
    } finally {
      window.clearTimeout(historyTimeout);
    }
  }
  return product;
}

function imageUrlAtWidth(url, width) {
  const [path, query] = String(url).split('?');
  const sizedPath = path.replace(/(\.[a-z0-9]+)$/i, `_${width}x$1`);
  return query ? `${sizedPath}?${query}` : sizedPath;
}

async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

function polishProductCount(count) {
  const n = Math.abs(Number(count)) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return 'zapisany produkt';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'zapisane produkty';
  return 'zapisanych produktów';
}

function setWishlistCount(container, count) {
  const countEl = container.querySelector('[data-du-wishlist-total]');
  const labelEl = container.querySelector('[data-du-wishlist-label]');
  if (countEl) countEl.textContent = String(count);
  if (labelEl) labelEl.textContent = polishProductCount(count);
}

function cardMarkup(product, savedHandle = product.handle) {
  const availableVariant = product.variants.find((variant) => variant.available);
  const variant = availableVariant || product.variants[0];
  const sku = variant && variant.sku ? String(variant.sku).trim() : '';
  const image = product.featured_image
    ? `<img src="${escapeHtml(imageUrlAtWidth(product.featured_image, 600))}" srcset="${escapeHtml(
        imageUrlAtWidth(product.featured_image, 320)
      )} 320w, ${escapeHtml(imageUrlAtWidth(product.featured_image, 480))} 480w, ${escapeHtml(
        imageUrlAtWidth(product.featured_image, 600)
      )} 600w" sizes="(max-width: 749px) 46vw, 320px" alt="${escapeHtml(
        product.title
      )}" loading="lazy" width="600" height="600">`
    : '<span class="du-cat__placeholder" aria-hidden="true"></span>';

  const compare =
    variant?.compare_at_price && variant.compare_at_price > variant.price
      ? `<s class="du-wishlist__compare">${money(variant.compare_at_price)}</s>`
      : '';

  const availability = availableVariant
    ? `<span class="du-wishlist__stock du-wishlist__stock--in">${escapeHtml(t('inStock'))}</span>`
    : `<span class="du-wishlist__stock du-wishlist__stock--out">${escapeHtml(t('outOfStock'))}</span>`;

  const skuMarkup = sku
    ? `<p class="du-wishlist__sku">${escapeHtml(sku)}</p>`
    : '';

  const tags = Array.isArray(product.tags)
    ? product.tags.map((tag) => String(tag).trim().toLowerCase())
    : [];
  const isOutlet = tags.includes('outlet');
  const isPromo = tags.includes('promocja') || tags.includes('promocje') || tags.includes('promo');
  const isSelect = tags.includes('durasan-select') || tags.includes('durasan select');
  const ribbonStyle =
    'position:absolute;top:0;left:0;margin:0;padding:0;overflow:visible;border-top-left-radius:12px;border-bottom-right-radius:8px;box-shadow:none;filter:none;transform:none';
  let corner = '';
  if (isOutlet) {
    corner = `<span class="du-premium-ribbon du-premium-ribbon--flush du-corner-badge du-corner-badge--outlet" style="${ribbonStyle}" data-du-badge="outlet"><span class="du-premium-ribbon__text">Outlet</span></span>`;
  } else if (isPromo) {
    corner = `<span class="du-premium-ribbon du-premium-ribbon--flush du-corner-badge du-corner-badge--promo" style="${ribbonStyle}" data-du-badge="promo"><span class="du-premium-ribbon__text">Promocja</span></span>`;
  } else if (isSelect) {
    corner = `<span class="du-premium-ribbon du-premium-ribbon--flush" style="${ribbonStyle}" data-du-badge="select"><span class="du-premium-ribbon__text">Najwyższa jakość</span></span>`;
  }

  return `
    <li class="du-wishlist__item" data-handle="${escapeHtml(product.handle)}">
      <a class="du-wishlist__media du-media-plate" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">${corner}${image}</a>
      <div class="du-wishlist__body">
        ${skuMarkup}
        <h3 class="du-wishlist__title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
        <p class="du-wishlist__price">${money(variant?.price ?? product.price)}${compare ? ` ${compare}` : ''}</p>
        ${product.duPriceHistory ? `<span class="du-price-history" data-du-price-history data-product-id="${escapeHtml(product.id)}" data-variant-id="${escapeHtml(variant.id)}">${escapeHtml(product.duPriceHistory)}</span>` : ''}
        <div class="du-wishlist__meta">
          ${availability}
        </div>
        <div class="du-wishlist__actions">
          ${
            availableVariant
              ? `<button type="button" class="du-btn du-btn--solid" data-du-wish-add="${availableVariant.id}">${escapeHtml(
                  t('addToCart')
                )}</button>`
              : ''
          }
          <button type="button" class="du-wishlist__remove" data-du-wish-remove="${escapeHtml(savedHandle)}">
            ${escapeHtml(t('remove'))}
          </button>
        </div>
      </div>
    </li>`;
}

function unavailableCardMarkup(handle) {
  return `
    <li class="du-wishlist__item" data-handle="${escapeHtml(handle)}">
      <div class="du-wishlist__body">
        <h3 class="du-wishlist__title">Nie udało się załadować produktu</h3>
        <p class="du-wishlist__sku">${escapeHtml(handle)}</p>
        <div class="du-wishlist__actions">
          <button type="button" class="du-btn du-btn--solid" data-du-wish-retry>Ponów próbę</button>
          <button type="button" class="du-wishlist__remove" data-du-wish-remove="${escapeHtml(handle)}">${escapeHtml(t('remove'))}</button>
        </div>
      </div>
    </li>`;
}

function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );
}

const renderVersions = new WeakMap();

async function renderWishlistPage(container) {
  const version = (renderVersions.get(container) || 0) + 1;
  renderVersions.set(container, version);
  const handles = read();
  const list = container.querySelector('[data-du-wishlist-list]');
  const empty = container.querySelector('[data-du-wishlist-empty]');

  if (!list || !empty) return;

  if (handles.length === 0) {
    list.innerHTML = '';
    list.hidden = true;
    empty.hidden = false;
    list.removeAttribute('aria-busy');
    setWishlistCount(container, 0);
    return;
  }

  empty.hidden = true;
  list.hidden = false;
  list.setAttribute('aria-busy', 'true');

  const results = await mapWithConcurrency(handles, 6, (handle) => fetchProduct(handle).catch(() => null));

  // A removal or another tab may have changed the list while requests were in flight.
  if (renderVersions.get(container) !== version) return;

  // A failed request does not prove that a product was removed. Keep the saved
  // handle and let the customer retry or remove it explicitly. Outlet products
  // are regular saved products here, just as they are on collection cards.
  const availabilityRank = (product) => product?.available === true ? 0 : product?.available === false ? 2 : 1;
  list.innerHTML = results
    .map((product, index) => ({ product, handle: handles[index] }))
    .sort((a, b) => availabilityRank(a.product) - availabilityRank(b.product))
    .map(({ product, handle }) => product
      ? cardMarkup(product, handle)
      : unavailableCardMarkup(handle))
    .join('');
  list.removeAttribute('aria-busy');
  setWishlistCount(container, handles.length);
}

async function addToCart(variantId, button) {
  const id = Number(variantId);
  if (!id || button.disabled) return;
  const original = button.textContent;
  button.disabled = true;
  button.textContent = t('adding') || original;
  let didAdd = false;
  let cartSynced = false;

  const deferred = CartLinesUpdateEvent.createPromise();
  // Consumers redraw the count and drawer from the same completed request.
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(id), quantity: 1 }],
    promise: deferred.promise,
  }));

  try {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId).filter(Boolean))];
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart request failed');
    didAdd = true;
    const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('Cart refresh failed');
    const cart = await cartResponse.json();
    cartSynced = true;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: data.sections, source: 'durasan-wishlist', didError: false },
    });
    button.textContent = t('added') || original;
  } catch (error) {
    deferred.reject(error);
    if (didAdd) {
      // The mutation succeeded. Recover the view without inviting a duplicate add.
      button.textContent = t('added') || original;
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } else {
      document.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Add to cart failed', code: 'INVALID' }));
      button.textContent = t('error') || original;
    }
  } finally {
    if (!didAdd || cartSynced) {
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = original;
      }, 2200);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Inicjalizacja                                                        */
/* ------------------------------------------------------------------ */

function refresh() {
  syncAllButtons();
  syncCounters();

  const page = document.querySelector('[data-du-wishlist]');
  if (page) renderWishlistPage(page);
}

document.addEventListener('click', (event) => {
  const wish = event.target.closest('[data-du-wish]');
  if (wish) {
    event.preventDefault();
    event.stopPropagation();
    toggle(wish.dataset.productHandle);
    return;
  }

  const removeBtn = event.target.closest('[data-du-wish-remove]');
  if (removeBtn) {
    event.preventDefault();
    event.stopPropagation();
    removeBtn.closest('.du-wishlist__item')?.remove();
    remove(removeBtn.dataset.duWishRemove);
    return;
  }

  const retryBtn = event.target.closest('[data-du-wish-retry]');
  if (retryBtn) {
    event.preventDefault();
    const page = retryBtn.closest('[data-du-wishlist]');
    if (page) renderWishlistPage(page);
    return;
  }

  const addBtn = event.target.closest('[data-du-wish-add]');
  if (addBtn) {
    event.preventDefault();
    addToCart(addBtn.dataset.duWishAdd, addBtn);
  }
}, { capture: true });

document.addEventListener('durasan:wishlist:change', refresh);
document.addEventListener('durasan:sibling:changed', refresh);

// Lista jest wspólna dla kart otwartych w wielu zakładkach.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) refresh();
});

// Sekcje doładowywane przez Section Rendering API wnoszą nowe serduszka.
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === 1 && node.querySelector?.('[data-du-wish]')) {
        syncAllButtons(node);
      }
    }
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    refresh();
    observer.observe(document.body, { childList: true, subtree: true });
  });
} else {
  refresh();
  observer.observe(document.body, { childList: true, subtree: true });
}
